import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { ticketKeys, trackerKeys } from './keys'
import { isAdminViewer } from '@/lib/scope'
import type { Ticket, TicketDetail, TicketMessage } from '@/types/ticket'
import type { TrackerStatus, TrackerTicket } from '@/types/tracker'

// useTicket's cache key always carries an asUser suffix (see queries/tickets.ts).
// Neither reply nor admin-patch ever runs under the view-as lens (ReplyComposer
// hides there, AdminControls only mounts on the admin's own non-lensed route),
// so the exact key they need to read/write is always the unlensed `null` slot.
const detailKey = (ticketId: string) => [...ticketKeys.detail(ticketId), null] as const

export function useCreateTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { subject: string; body: string; attachments?: unknown[]; context?: unknown }) =>
      api<Ticket>('/support-tickets', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ticketKeys.all }),
  })
}

interface ReplyVars {
  body: string
  attachments?: unknown[]
  isInternal?: boolean
}

// Optional viewer profile lets the optimistic message carry a real name
// instead of "Unknown" while the round-trip is in flight.
export function useReply(ticketId: string, viewer?: { name?: string; isAdmin?: boolean }) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ReplyVars) =>
      api<TicketMessage>(`/support-tickets/${ticketId}/messages`, { method: 'POST', body }),
    onMutate: async vars => {
      await qc.cancelQueries({ queryKey: ticketKeys.detail(ticketId) })
      const prev = qc.getQueryData<TicketDetail>(detailKey(ticketId))
      if (prev) {
        // Borrow the viewer's hydrated author object from an earlier message
        // when available — keeps avatar/staff badge consistent.
        const ownPrior = prev.messages.find(m => m.author?._id === prev.me._id)
        const optimistic: TicketMessage = {
          _id: `optimistic-${Math.random().toString(36).slice(2)}`,
          ticketId,
          authorId: prev.me._id,
          authorRole: isAdminViewer(prev.viewerRelation) ? 'admin' : 'user',
          author: ownPrior?.author ?? { _id: prev.me._id, name: viewer?.name, isAdmin: viewer?.isAdmin },
          kind: 'reply',
          isInternal: Boolean(vars.isInternal),
          body: vars.body,
          attachments: (vars.attachments ?? []) as TicketMessage['attachments'],
          createdAt: new Date().toISOString(),
        }
        qc.setQueryData<TicketDetail>(detailKey(ticketId), {
          ...prev,
          messages: [...prev.messages, optimistic],
        })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(detailKey(ticketId), ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) })
      // The presentation deck's quick comment box posts through this same
      // mutation (see PresentationView) — a new comment must land in the
      // deck's recentMessages on refetch, same reasoning as useAdminPatch above.
      qc.invalidateQueries({ queryKey: trackerKeys.presentations })
    },
  })
}

interface PatchVars {
  status?: string
  priority?: string
  assignedTo?: string | null
  subject?: string
}

export function useAdminPatch(ticketId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PatchVars) =>
      api<{ ticket: Ticket }>(`/support-tickets/${ticketId}`, { method: 'PATCH', body }),
    onMutate: async vars => {
      await qc.cancelQueries({ queryKey: ticketKeys.detail(ticketId) })
      const prev = qc.getQueryData<TicketDetail>(detailKey(ticketId))
      if (prev) {
        const ticket = { ...prev.ticket }
        if (vars.status) ticket.status = vars.status as Ticket['status']
        if (vars.priority) ticket.priority = vars.priority as Ticket['priority']
        if (vars.subject) ticket.subject = vars.subject
        if (vars.assignedTo !== undefined) {
          ticket.assignedTo = vars.assignedTo
          // Hydrated assignee object refreshes on settle; clearing on unassign
          // keeps the panel consistent in the meantime.
          if (vars.assignedTo === null) ticket.assignee = null
        }
        qc.setQueryData<TicketDetail>(detailKey(ticketId), { ...prev, ticket })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(detailKey(ticketId), ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) })
      qc.invalidateQueries({ queryKey: ticketKeys.all })
      // The tracker surfaces read a promoted ticket's subject/priority from
      // their OWN key families, not ticketKeys — TrackerDetailView's subject
      // edit and TrackerPropertyBand's priority select both patch through
      // here while the route renders useTrackerTicket's ['tracker','detail',…]
      // entry, so without these two the edit only appears after a reload.
      // Same narrowing as invalidateTicketAndTracker: lists/details, never
      // trackerKeys.all, which would drag in the enablement probe and the
      // categories cache.
      qc.invalidateQueries({ queryKey: trackerKeys.lists })
      qc.invalidateQueries({ queryKey: trackerKeys.details })
      // A status/priority/subject edit made from the presentation deck (the
      // only caller that can touch `subject` — see PresentationView) must
      // refetch the deck's single query; AdminControls' own ticket-detail
      // route never mounts a presentation cache entry, so this is a no-op
      // there.
      qc.invalidateQueries({ queryKey: trackerKeys.presentations })
    },
  })
}

// The reporter closing their own ticket — POST /:id/close, not the admin-only
// PATCH. The server re-checks that the caller raised the ticket, so this is
// only ever offered to viewerRelation === 'owner' as a courtesy, never as the
// enforcement (see supportTicket.service#closeOwnTicket).
export function useCloseOwnTicket(ticketId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api<{ ticket: Ticket }>(`/support-tickets/${ticketId}/close`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) })
      qc.invalidateQueries({ queryKey: ticketKeys.all })
    },
  })
}

export function useUploadAttachment(ticketId: string) {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return api<{ s3Key: string; filename: string; mimeType: string; size: number }>(
        `/support-tickets/${ticketId}/attachments`,
        { method: 'POST', body: form },
      )
    },
  })
}

// A promotion/demotion/status/tracker-field change touches both surfaces at
// once: the ticket detail (tracker block, derived status) and every tracker
// list this ticket appears or stops appearing in. Missing either invalidation
// is how a promoted ticket goes on showing "unpromoted" in one view or the
// other.
//
// Deliberately trackerKeys.lists/details, NOT trackerKeys.all: `all` is a
// prefix match, so it would also catch the enablement probe
// (['tracker','enabled',…]) and the categories cache
// (['tracker','categories',…]) and refetch both on every single mutation here
// for no reason — neither one's answer ever changes as a side effect of
// promoting/demoting/re-statusing/editing a ticket.
function invalidateTicketAndTracker(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ticketKeys.all })
  qc.invalidateQueries({ queryKey: trackerKeys.lists })
  qc.invalidateQueries({ queryKey: trackerKeys.details })
  // The presentation deck's tracker-status/impact edits (useSetTrackerStatus,
  // useUpdateTracker) go through this same helper — see PresentationView.
  qc.invalidateQueries({ queryKey: trackerKeys.presentations })
}

interface PromoteVars {
  category: string
  impact?: string
}

export function usePromoteTicket(ticketId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PromoteVars) =>
      api<TrackerTicket>(`/support-tickets/${ticketId}/promote`, { method: 'POST', body }),
    onSuccess: () => invalidateTicketAndTracker(qc),
  })
}

// Staff-only: adds to the company's tracker categories (the promote dialog's
// "New category" option). The response is the new effective list, written
// straight into the categories cache the dialog's select reads.
export function useAddTrackerCategory(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (category: string) =>
      api<{ category: string; categories: string[] }>('/support-tickets/tracker/categories', {
        method: 'POST',
        body: { companyId, category },
      }),
    onSuccess: data => qc.setQueryData(trackerKeys.categories(companyId), data.categories),
  })
}

export function useDemoteTicket(ticketId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api<Ticket>(`/support-tickets/${ticketId}/demote`, { method: 'POST' }),
    onSuccess: () => invalidateTicketAndTracker(qc),
  })
}

interface SetTrackerStatusVars {
  status: TrackerStatus
}

export function useSetTrackerStatus(ticketId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SetTrackerStatusVars) =>
      api<TrackerTicket>(`/support-tickets/${ticketId}/tracker-status`, { method: 'POST', body }),
    onSuccess: () => invalidateTicketAndTracker(qc),
  })
}

interface UpdateTrackerVars {
  category?: string
  impact?: string
  expectedDate?: string | null
}

// Staff-only whitelist edit of the tracker block's editable fields — devStage
// and status are never sent from here (they have their own dedicated,
// derived/transition-checked paths), matching the server's whitelist in
// supportTicket.controller#patchTracker.
export function useUpdateTracker(ticketId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateTrackerVars) =>
      api<TrackerTicket>(`/support-tickets/${ticketId}/tracker`, { method: 'PATCH', body }),
    onSuccess: () => invalidateTicketAndTracker(qc),
  })
}

// Staff-only plain-text export for pasting into an LLM — GET
// /support-tickets/:id/copy-payload. A mutation, not a query: the endpoint
// presigns attachment URLs on every call (see supportTicket.service's
// COPY_PAYLOAD_TTL_SECONDS), so it must only ever fire on an explicit click,
// never on mount/re-render the way a cached query would.
export function useCopyPayload(ticketId: string) {
  return useMutation({
    mutationFn: () => api<{ text: string }>(`/support-tickets/${ticketId}/copy-payload`),
  })
}

// ofetch's own FetchError.message is a generic "[POST] url: 409 Conflict" —
// the API's actual message (e.g. an illegal-transition explanation) lives in
// the parsed body at err.data.error.message (see etainabl-api's
// productionErrors handler). Falls back to a plain Error's .message, then the
// caller-supplied default, so callers never have to special-case either shape.
export function extractErrorMessage(err: unknown, fallback: string): string {
  const shaped = err as { data?: { error?: { message?: string } }; message?: string } | null | undefined
  return shaped?.data?.error?.message ?? shaped?.message ?? fallback
}
